import * as React from 'react';
import { Route, Switch } from 'react-router';
import { PageContext } from '../../common/contexts/PageContext';
import { IPageName } from '../../common/types/apiSchema';
import { getPaths } from '../../constants/paths/paths';
import { ICancellationPageProps } from '../CancellationPage/CancellationPage.interfaces';
import { ISalePageProps } from '../SalePage/SalePage';
import { ITravelBirdSalePageProps } from '../TravelBirdSalePage/TravelBirdSalePage.interfaces';

const CurrentSalesPage: React.LazyExoticComponent<React.FunctionComponent<any>> = React.lazy(() =>
  import(/* webpackChunkName: "CurrentSalesPage" */ '../CurrentSalesPage')
);

const SearchResultsPage: React.LazyExoticComponent<React.FunctionComponent<{}>> = React.lazy(() =>
  import(/* webpackChunkName: "SearchResultsPage" */ '../SearchResultsPage')
);

const CollectionPage: React.LazyExoticComponent<React.FunctionComponent<{}>> = React.lazy(() =>
  import(/* webpackChunkName: "CollectionPage" */ '../CollectionPage')
);

const SalePage: React.LazyExoticComponent<React.FunctionComponent<any>> = React.lazy(() =>
  import(/* webpackChunkName: "SalePage" */ '../SalePage')
);

const CancellationPage: React.LazyExoticComponent<React.FunctionComponent<{}>> = React.lazy(() =>
  import(/* webpackChunkName: "CancellationPage" */ '../CancellationPage')
);

const BookingsPage: React.LazyExoticComponent<React.FunctionComponent<{}>> = React.lazy(() =>
  import(/* webpackChunkName: "BookingPage" */ '../BookingsPage')
);

const BookingDetailsPage: React.LazyExoticComponent<React.FunctionComponent<{}>> = React.lazy(() =>
  import(/* webpackChunkName: "BookingDetailsPage" */ '../BookingDetailsPage')
);

const LogoutContainer: React.LazyExoticComponent<React.FunctionComponent<{}>> = React.lazy(() =>
  import(/* webpackChunkName: "LogoutContainer" */ '../LogoutContainer')
);

const TravelBirdSalePage: React.LazyExoticComponent<React.FunctionComponent<
  ITravelBirdSalePageProps
>> = React.lazy(() => import(/* webpackChunkName: "TravelBirdSalePage" */ '../TravelBirdSalePage'));

interface ICauldronRouteProps {
  page: IPageName;
  children: JSX.Element;
}

const CauldronPage: React.FC<ICauldronRouteProps> = ({ page, children }) => {
  return (
    <PageContext.Provider value={page}>
      <React.Suspense fallback={<div />}>{children}</React.Suspense>
    </PageContext.Provider>
  );
};

/**
 * Application Route. Sets up ApolloProvider and Wrappers.
 */
const ApplicationRouter: React.FunctionComponent<
  ITravelBirdSalePageProps | ISalePageProps | ICancellationPageProps
> = props => {
  return (
    <Switch>
      <Route exact={true} path={getPaths(IPageName.instantAccess)}>
        <CauldronPage page={IPageName.instantAccess}>
          <LogoutContainer />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.collections)}>
        <CauldronPage page={IPageName.collections}>
          <CollectionPage />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.searchResults)}>
        <CauldronPage page={IPageName.searchResults}>
          <SearchResultsPage />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.salePage)}>
        <CauldronPage page={IPageName.salePage}>
          <SalePage
            saleId={(props as ISalePageProps).saleId}
            isPreview={(props as ISalePageProps).isPreview}
            loginUserFunction={(props as ISalePageProps).loginUserFunction}
          />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.travelBirdSalePage)}>
        <CauldronPage page={IPageName.travelBirdSalePage}>
          <TravelBirdSalePage
            travelBirdOfferId={(props as ITravelBirdSalePageProps).travelBirdOfferId}
          />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.cancellationPage)}>
        <CauldronPage page={IPageName.cancellationPage}>
          <CancellationPage />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.bookingDetailsPage)}>
        <CauldronPage page={IPageName.bookingDetailsPage}>
          <BookingDetailsPage />
        </CauldronPage>
      </Route>
      <Route path={getPaths(IPageName.bookingsPage)}>
        <CauldronPage page={IPageName.bookingsPage}>
          <BookingsPage />
        </CauldronPage>
      </Route>
      <Route>
        <React.Suspense fallback={<div />}>
          <PageContext.Provider value={IPageName.currentSales}>
            <CurrentSalesPage />
          </PageContext.Provider>
        </React.Suspense>
      </Route>
    </Switch>
  );
};

ApplicationRouter.displayName = 'ApplicationRouter';

export default ApplicationRouter;
